import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import DefaultWebLayout from './layouts/DefaultWebLayout.jsx';
import AdminLayout from './layouts/AdminLayout.jsx';
import ConnectorRegistrationLayout from './layouts/ConnectorRegistrationLayout.jsx';
import PublicInfoLayout from './layouts/PublicInfoLayout.jsx';
import lazyWithRetry from './utils/lazyWithRetry.js';
import useModalScrollLock from './hooks/useModalScrollLock.js';
import RoleRoute from './components/RoleRoute.jsx';
import CapabilityRoute from './components/CapabilityRoute.jsx';
import './App.scss';

// Pages are generated per the design reference by ui-components-from-reference
// and registered here by slug, inside the DefaultWebLayout route group, e.g.:
//   const ConnectorDashboard = lazyWithRetry(() => import('./pages/connector-dashboard/index.jsx'));
//   <Route path="connector-dashboard" element={<ConnectorDashboard />} />
// Public auth pages — registered ahead of any route guards so anonymous users
// can reach them. Login uses the _connector-registration-layout shell.
const LoginPage = lazyWithRetry(() => import('./pages/login/index.jsx'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/forgot-password/index.jsx'));
const VerifyConnectorPage = lazyWithRetry(() => import('./pages/verify-connector/index.jsx'));
const ConnectorRegistrationPage = lazyWithRetry(() => import('./pages/connector-registration/index.jsx'));
const SocialCallbackPage = lazyWithRetry(() => import('./pages/social-callback/index.jsx'));
// Public home + legal pages — no sign-in (Google OAuth verification).
const LandingPage = lazyWithRetry(() => import('./pages/public/LandingPage.jsx'));
const PolicyPage = lazyWithRetry(() => import('./pages/public/PolicyPage.jsx'));
// Connector self-service — role-gated.
const ConnectorOnboardingPage = lazyWithRetry(() => import('./pages/connector-onboarding/index.jsx'));
const ConnectorProfilePage = lazyWithRetry(() => import('./pages/connector-profile/index.jsx'));
const ConnectorDashboardPage = lazyWithRetry(() => import('./pages/connector-dashboard/index.jsx'));
const ConnectorReferralsPage = lazyWithRetry(() => import('./pages/connector-referrals/index.jsx'));
const ConnectorCommissionsPage = lazyWithRetry(() => import('./pages/connector-commissions/index.jsx'));
const NewReferralWizardPage = lazyWithRetry(() => import('./pages/new-referral/index.jsx'));
// Compliance / Accreditation Management — admin (Compliance Officer) only.
const ConnectorAccreditationPage = lazyWithRetry(() => import('./pages/connector-accreditation/index.jsx'));
// Account Management referral intake — admin (staff) only.
const ReferralQueuePage = lazyWithRetry(() => import('./pages/referral-queue/index.jsx'));
const ReferralDetailsPage = lazyWithRetry(() => import('./pages/referral-details/index.jsx'));
// Commission configuration — Group Admin only.
const CommissionConfigurationPage = lazyWithRetry(() => import('./pages/commission-configuration/index.jsx'));
// Commission processing — Loans Officer (admin) only.
const CommissionProcessingPage = lazyWithRetry(() => import('./pages/commission-processing/index.jsx'));
// Finance export & handoff — Finance User (admin) only.
const ExportHandoffPage = lazyWithRetry(() => import('./pages/export-handoff/index.jsx'));
// Group Admin administration & security — admin only.
const UserAdministrationPage = lazyWithRetry(() => import('./pages/user-administration-security/index.jsx'));
const RolePermissionPage = lazyWithRetry(() => import('./pages/role-permission-management/index.jsx'));
const AuditLogsPage = lazyWithRetry(() => import('./pages/audit-logs-security-monitoring/index.jsx'));
const NotificationManagementPage = lazyWithRetry(() => import('./pages/notification-management/index.jsx'));
const AdminDashboardPage = lazyWithRetry(() => import('./pages/admin-dashboard/index.jsx'));
const BrandingManagementPage = lazyWithRetry(() => import('./pages/branding-management/index.jsx'));
const IntegrationsManagementPage = lazyWithRetry(() => import('./pages/integrations-management/index.jsx'));
// Management reports — System Administrator only (SME Concern #18).
const ReportsManagementPage = lazyWithRetry(() => import('./pages/reports-management/index.jsx'));
const ConnectorReportPage = lazyWithRetry(() => import('./pages/reports-management/connector-report.jsx'));
const BranchManagementPage = lazyWithRetry(() => import('./pages/branch-management/index.jsx'));
// Super Admin group console — full-page, own shell (no connector/admin nav).
const SuperAdminPage = lazyWithRetry(() => import('./pages/superadmin/index.jsx'));
const NotFound = lazyWithRetry(() => import('./pages/status/NotFound.jsx'));
const Unauthorized = lazyWithRetry(() => import('./pages/status/Unauthorized.jsx'));
const Forbidden = lazyWithRetry(() => import('./pages/status/Forbidden.jsx'));
const ServerError = lazyWithRetry(() => import('./pages/status/ServerError.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  // QA 8384 — locks the page behind any open modal, app-wide. Mounted at the
  // root rather than per shell so public pages (login, registration, onboarding)
  // are covered too.
  useModalScrollLock();

  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public accreditation cross-check behind the connector badge QR
                (QA 7838). Standalone and unauthenticated by design — scanned by
                members of the public, so it sits outside every layout and guard. */}
            <Route path="verify/c/:token" element={<VerifyConnectorPage />} />

            {/* Public home and legal pages. Google's OAuth verification needs
                the home page, privacy policy and terms reachable without
                signing in; `/` used to redirect to /login. A signed-in user
                visiting `/` is sent on to their workspace by LandingPage. */}
            <Route element={<PublicInfoLayout />}>
              <Route index element={<LandingPage />} />
              <Route path="privacy" element={<PolicyPage which="privacy" />} />
              <Route path="terms" element={<PolicyPage which="terms" />} />
            </Route>

            {/* Public auth routes — before the guarded groups. */}
            <Route element={<ConnectorRegistrationLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="connector-registration" element={<ConnectorRegistrationPage />} />
              {/* Google / Facebook round trip lands here (8207). */}
              <Route path="social-callback" element={<SocialCallbackPage />} />
            </Route>

            {/* Group Admin / Super Admin group console — standalone full-page.
                Group-wide oversight: super_admin (Group Admin) + read-only
                group_viewer. The per-company System Admin (role 'admin') does
                NOT have access. */}
            <Route element={<RoleRoute roles={['super_admin', 'group_viewer']} />}>
              <Route path="superadmin" element={<SuperAdminPage />} />
            </Route>

            {/* Admin / internal desktop routes — use AdminLayout (sidebar, no PWA).
                RoleRoute gates the coarse platform role; CapabilityRoute then
                enforces the per-title capability map (config/access.js) so a
                narrow-title admin can't URL-navigate into another workspace. */}
            <Route element={<RoleRoute roles={['admin']} />}>
              <Route element={<CapabilityRoute />}>
                <Route element={<AdminLayout />}>
                  <Route path="connector-accreditation" element={<ConnectorAccreditationPage />} />
                  <Route path="connector-accreditation-management" element={<ConnectorAccreditationPage />} />
                  <Route path="referral-queue" element={<ReferralQueuePage />} />
                  <Route path="referral-details/:id" element={<ReferralDetailsPage />} />
                  <Route path="commission-configuration" element={<CommissionConfigurationPage />} />
                  <Route path="commission-configuration-approvals" element={<CommissionConfigurationPage />} />
                  <Route path="commission-processing-queue" element={<CommissionProcessingPage />} />
                  <Route path="commission-processing" element={<CommissionProcessingPage />} />
                  <Route path="export-handoff-management" element={<ExportHandoffPage />} />
                  <Route path="export-handoff" element={<ExportHandoffPage />} />
                  <Route path="user-administration-security" element={<UserAdministrationPage />} />
                  <Route path="role-permission-management" element={<RolePermissionPage />} />
                  <Route path="audit-logs-security-monitoring" element={<AuditLogsPage />} />
                  <Route path="notification-management" element={<NotificationManagementPage />} />
                  <Route path="admin-dashboard" element={<AdminDashboardPage />} />
                  <Route path="branding-management" element={<BrandingManagementPage />} />
                  <Route path="integrations-management" element={<IntegrationsManagementPage />} />
                  <Route path="branch-management" element={<BranchManagementPage />} />
                  <Route path="reports-management" element={<ReportsManagementPage />} />
                  {/* Drill-down from the Top Performer Connectors table (QA 8145) —
                      inside the same guarded block as the summary report. */}
                  <Route path="reports/connectors/:userId" element={<ConnectorReportPage />} />
                </Route>
              </Route>
            </Route>

            <Route element={<DefaultWebLayout />}>

              {/* Connector self-service. These render the SIGNED-IN connector's
                  own data, so they are connector-only — access.js lists them in
                  CONNECTOR_ONLY and withholds them even from the System Admin
                  superuser. RoleRoute alone was not enough: every internal staff
                  member is role 'admin' (their `title` is what narrows them), so
                  the coarse check passed and a Loans Officer could reach the
                  Connector Dashboard by URL or Back button while the nav hid the
                  link (QA 7861). CapabilityRoute applies the same capability map
                  the nav uses, and sends a denied user to their own workspace. */}
              <Route element={<RoleRoute roles={['connector', 'admin']} />}>
                <Route element={<CapabilityRoute />}>
                  <Route path="connector-onboarding" element={<ConnectorOnboardingPage />} />
                  <Route path="connector-profile" element={<ConnectorProfilePage />} />
                  <Route path="connector-dashboard" element={<ConnectorDashboardPage />} />
                  <Route path="connector-referrals" element={<ConnectorReferralsPage />} />
                  <Route path="connector-commissions" element={<ConnectorCommissionsPage />} />
                  <Route path="new-referral" element={<NewReferralWizardPage />} />
                  <Route path="new-referral-wizard" element={<NewReferralWizardPage />} />
                </Route>
              </Route>

              {/* Status pages */}
              <Route path="401" element={<Unauthorized />} />
              <Route path="403" element={<Forbidden />} />
              <Route path="500" element={<ServerError />} />

              {/* Router catch-all — dangling menu links land here until their
                  page component is generated. */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
